import React from 'react';

import { cn } from '@easydatasolution/ui-react/lib/utils';
import { GatsbyImage, IGatsbyImageData, GatsbyImageProps } from 'gatsby-plugin-image';

interface BaseImageData {
	extension: string;
}

interface SvgImageData extends BaseImageData {
	extension: 'svg';
	publicURL: string;
	childImageSharp?: never;
}

interface RegularImageData extends BaseImageData {
	extension: string;
	childImageSharp: {
		gatsbyImageData: IGatsbyImageData;
	};
	publicURL?: string;
}

export type ImageData = SvgImageData | RegularImageData;

function isSvgImage(image: ImageData): image is SvgImageData {
	return image.extension === 'svg';
}

type CommonImageProps = Pick<
	React.ImgHTMLAttributes<HTMLImageElement>,
	| 'title'
	| 'draggable'
	| 'loading'
	| 'className'
	| 'style'
	| 'id'
	| 'role'
	| 'aria-label'
	| 'onClick'
	| 'onError'
	| 'width'
	| 'height'
>;

interface ImageProps extends CommonImageProps {
	/**
	 * The image data from a GraphQL query
	 */
	image: ImageData;
	/**
	 * Alt text for the image. Required for accessibility.
	 */
	alt?: string | null;
	/**
	 * Whether to use object-fit: contain for the image
	 * @default false
	 */
	contain?: boolean;
	/**
	 * Additional props specific to the HTML img element
	 */
	imgProps?: Omit<React.ImgHTMLAttributes<HTMLImageElement>, keyof CommonImageProps | 'alt'>;
	/**
	 * Additional props specific to the GatsbyImage component
	 */
	gatsbyImageProps?: Omit<GatsbyImageProps, keyof CommonImageProps | 'image' | 'alt'>;
}

/**
 * A component that renders either an SVG or a regular image processed by Gatsby Image.
 * Common image props like alt, title, draggable, etc. can be passed directly.
 * Component-specific props can be passed via imgProps or gatsbyImageProps.
 */
export function Image({
	image,
	// Common image props
	alt: _alt,
	title,
	draggable,
	loading = 'eager',
	className,
	style,
	id,
	role,
	'aria-label': ariaLabel,
	// Specific props
	imgProps = {},
	gatsbyImageProps = {},
}: ImageProps) {
	const alt = _alt || '';
	const commonProps = {
		title,
		draggable,
		loading,
		className: cn('h-full w-full', className),
		style,
		id,
		role,
		'aria-label': ariaLabel,
	};

	if (isSvgImage(image)) {
		if (!image.publicURL) {
			throw new Error('publicURL is required for SVG images');
		}
		return <img src={image.publicURL} alt={alt} {...commonProps} {...imgProps} />;
	}

	return (
		<GatsbyImage
			image={image.childImageSharp.gatsbyImageData}
			alt={alt}
			objectFit={gatsbyImageProps.objectFit ?? 'contain'}
			{...commonProps}
			{...gatsbyImageProps}
		/>
	);
}
